import { httpClient } from '@/shell';
import {
  withIdempotency,
  type CatalogOrderSaveInput,
  type CatalogOrderSaveResponse,
  type AuctionItem,
  type Bundle,
  type Category,
  type ItemCreateInput,
  type ItemUpdateInput,
  type BundleCreateInput,
  type BundleUpdateInput,
  type BundleLifecycleResult,
  type BulkItemResult,
  type ItemImportRow,
  type ReapplyDefaultsResponse,
  type PresignedSlot,
} from '@neon-one/auctions-types';

export type BulkActionResult = BulkItemResult;

// be-012 admin catalog endpoints. The wire types ship in
// @neon-one/auctions-types/wire/admin/*. AI-assist stays loose-typed
// pending be-012 wire updates; bulk import is typed against
// ItemImportRow (GT1-475 / S5).
export interface BulkImportRow {
  rowNumber: number;
  status: 'created' | 'failed';
  itemId: number | null;
  errors: { field: string; message: string }[];
}

export interface BulkImportResponse {
  results: BulkImportRow[];
  totalCreated: number;
  totalFailed: number;
}

export interface AiDescriptionResponse {
  descriptionHtml: string;
  modelId: string;
  cached: boolean;
}

export interface PaginatedItemsResult {
  items: AuctionItem[];
  totalCount: number;
  offset: number;
  limit: number;
}

export interface ItemListParams {
  offset?: number;
  limit?: number;
  search?: string;
  categoryId?: string;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
}

/** One member of a bundle, addressed the way the lifecycle routes address it. */
export interface BundleMemberRef {
  bundleId: number;
  itemId: number;
}

const postBundleLifecycle = async (
  path: string,
  idempotencyKey?: string
): Promise<BundleLifecycleResult> => {
  const resp = await httpClient().post<BundleLifecycleResult>(
    path,
    undefined,
    idempotencyKey ? withIdempotency(idempotencyKey) : undefined
  );
  return resp.data;
};

export const adminCatalogApi = {
  async getBidCounts(auctionUuid: string): Promise<Record<string, number>> {
    const resp = await httpClient().get<Record<string, number>>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/bid-counts`
    );
    return resp.data;
  },

  async listItems(auctionUuid: string): Promise<AuctionItem[]> {
    const resp = await httpClient().get<AuctionItem[] | { items: AuctionItem[] }>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items`
    );
    return Array.isArray(resp.data) ? resp.data : resp.data.items;
  },

  async listItemsPaginated(auctionUuid: string, params: ItemListParams = {}): Promise<PaginatedItemsResult> {
    const query = new URLSearchParams();
    if (params.offset != null) query.set('offset', String(params.offset));
    if (params.limit != null) query.set('limit', String(params.limit));
    if (params.search) query.set('search', params.search);
    if (params.categoryId) query.set('categoryId', params.categoryId);
    if (params.sortField) query.set('sortField', params.sortField);
    if (params.sortOrder) query.set('sortOrder', params.sortOrder);
    const qs = query.toString();
    const resp = await httpClient().get<PaginatedItemsResult>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items${qs ? `?${qs}` : ''}`
    );
    return resp.data;
  },

  async saveCatalogOrder(auctionUuid: string, input: CatalogOrderSaveInput): Promise<CatalogOrderSaveResponse> {
    const resp = await httpClient().put<CatalogOrderSaveResponse>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/catalog-order`,
      input
    );
    return resp.data;
  },

  async createItem(auctionUuid: string, input: ItemCreateInput, idempotencyKey: string): Promise<AuctionItem> {
    const resp = await httpClient().post<AuctionItem>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items`,
      input,
      withIdempotency(idempotencyKey)
    );
    return resp.data;
  },

  async updateItem(auctionUuid: string, itemId: number, input: ItemUpdateInput): Promise<AuctionItem> {
    const resp = await httpClient().patch<AuctionItem>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/${itemId}`,
      input
    );
    return resp.data;
  },

  async withdrawItem(auctionUuid: string, itemId: number): Promise<AuctionItem> {
    const resp = await httpClient().patch<AuctionItem>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/${itemId}?status=withdrawn`
    );
    return resp.data;
  },

  async deleteItem(auctionUuid: string, itemId: number): Promise<void> {
    await httpClient().delete(`/admin/auctions/${encodeURIComponent(auctionUuid)}/items/${itemId}`);
  },

  async bulkImport(
    auctionUuid: string,
    rows: ItemImportRow[],
    idempotencyKey: string
  ): Promise<BulkImportResponse> {
    // Backend returns { imported: [{index, id}], failed: [{index, fieldErrors}] }.
    // Frontend types expect { results, totalCreated, totalFailed } — adapt
    // at the seam so call sites stay stable.
    interface ServerImportResponse {
      imported: Array<{ index: number; id: number }>;
      failed: Array<{ index: number; fieldErrors: Record<string, string[]> }>;
    }
    const resp = await httpClient().post<ServerImportResponse>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/import`,
      { rows },
      withIdempotency(idempotencyKey)
    );
    const { imported, failed } = resp.data;
    const results: BulkImportRow[] = [
      ...imported.map(r => ({
        rowNumber: r.index + 1,
        status: 'created' as const,
        itemId: r.id,
        errors: [],
      })),
      ...failed.map(r => ({
        rowNumber: r.index + 1,
        status: 'failed' as const,
        itemId: null,
        errors: Object.entries(r.fieldErrors).flatMap(([field, msgs]) => msgs.map(message => ({ field, message }))),
      })),
    ].sort((a, b) => a.rowNumber - b.rowNumber);
    return {
      results,
      totalCreated: imported.length,
      totalFailed: failed.length,
    };
  },

  async aiDescription(auctionUuid: string, itemId: number): Promise<AiDescriptionResponse> {
    const resp = await httpClient().post<AiDescriptionResponse>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/${itemId}/ai-description`
    );
    return resp.data;
  },

  async listBundles(auctionUuid: string): Promise<Bundle[]> {
    const resp = await httpClient().get<Bundle[] | { items: Bundle[] }>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles`
    );
    return Array.isArray(resp.data) ? resp.data : resp.data.items;
  },

  async createBundle(auctionUuid: string, input: BundleCreateInput, idempotencyKey: string): Promise<Bundle> {
    const resp = await httpClient().post<Bundle>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles`,
      input,
      withIdempotency(idempotencyKey)
    );
    return resp.data;
  },

  async updateBundle(auctionUuid: string, bundleId: number, input: BundleUpdateInput): Promise<Bundle> {
    const resp = await httpClient().patch<Bundle>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles/${bundleId}`,
      input
    );
    return resp.data;
  },

  async deleteBundle(auctionUuid: string, bundleId: number): Promise<{ freedItemIds: number[] }> {
    const resp = await httpClient().delete<{ freedItemIds: number[] }>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles/${bundleId}`
    );
    return resp.data;
  },

  async publishBundle(auctionUuid: string, bundleId: number): Promise<Bundle> {
    const resp = await httpClient().post<Bundle>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles/${bundleId}/publish`
    );
    return resp.data;
  },

  async unpublishBundle(auctionUuid: string, bundleId: number): Promise<Bundle> {
    const resp = await httpClient().post<Bundle>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles/${bundleId}/unpublish`
    );
    return resp.data;
  },

  /**
   * Hide one member from bidders (GT1-446). One server transaction: any live
   * bid stack on the bundle is voided, every affected bidder is notified, the
   * bundle pauses, and its starting bid + FMV are re-summed over the members
   * bidders can still see.
   */
  async withdrawBundleMember(
    auctionUuid: string,
    member: BundleMemberRef,
    idempotencyKey: string
  ): Promise<BundleLifecycleResult> {
    return postBundleLifecycle(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles/${member.bundleId}/members/${member.itemId}/withdraw`,
      idempotencyKey
    );
  },

  /** Un-withdraw a member. Same consequences as withdrawal — a restore is a composition change too. */
  async restoreBundleMember(
    auctionUuid: string,
    member: BundleMemberRef,
    idempotencyKey: string
  ): Promise<BundleLifecycleResult> {
    return postBundleLifecycle(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles/${member.bundleId}/members/${member.itemId}/restore`,
      idempotencyKey
    );
  },

  /**
   * Detach a member from the bundle entirely (GT1-446) — unlike withdraw, the
   * item returns to the open catalog as a standalone lot rather than staying
   * bundle-scoped and hidden. Same void+pause+recalc consequences as withdraw.
   */
  async removeBundleMember(
    auctionUuid: string,
    member: BundleMemberRef,
    idempotencyKey: string
  ): Promise<BundleLifecycleResult> {
    return postBundleLifecycle(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles/${member.bundleId}/members/${member.itemId}/remove`,
      idempotencyKey
    );
  },

  /**
   * Add a member to a live bundle (GT1-446). Voids the item's own standalone
   * bid (if any) and the bundle's stack (if it has active bids), pausing the
   * bundle when either voided something.
   */
  async addBundleMember(
    auctionUuid: string,
    member: BundleMemberRef,
    idempotencyKey: string
  ): Promise<BundleLifecycleResult> {
    return postBundleLifecycle(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles/${member.bundleId}/members/${member.itemId}/add`,
      idempotencyKey
    );
  },

  /**
   * Accept bids on a paused bundle again. Refused with VALIDATION_FAILED below
   * two visible members; reopening an unpaused bundle is a no-op, so a repeat
   * click cannot fail (hence no idempotency key).
   */
  async reopenBundle(auctionUuid: string, bundleId: number): Promise<BundleLifecycleResult> {
    return postBundleLifecycle(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles/${bundleId}/reopen`
    );
  },

  /**
   * Withdraw the WHOLE bundle from the catalog. Composition stays intact —
   * `bundle_components` rows are untouched, unlike deleteBundle's dissolve —
   * but the bundle and every member item are hidden from bidders, and any
   * active bid on the bundle voids.
   */
  async withdrawBundle(auctionUuid: string, bundleId: number, reason?: string): Promise<BundleLifecycleResult> {
    const resp = await httpClient().post<BundleLifecycleResult>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles/${bundleId}/withdraw`,
      reason ? { reason } : undefined
    );
    return resp.data;
  },

  async publishItem(auctionUuid: string, itemId: number): Promise<AuctionItem> {
    const resp = await httpClient().post<AuctionItem>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/${itemId}/publish`
    );
    return resp.data;
  },

  async unpublishItem(auctionUuid: string, itemId: number): Promise<AuctionItem> {
    const resp = await httpClient().post<AuctionItem>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/${itemId}/unpublish`
    );
    return resp.data;
  },

  async duplicateItem(auctionUuid: string, itemId: number): Promise<AuctionItem> {
    const resp = await httpClient().post<AuctionItem>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/${itemId}/duplicate`
    );
    return resp.data;
  },

  async downloadItemQr(auctionUuid: string, itemId: number): Promise<Blob> {
    const resp = await httpClient().get(`/admin/auctions/${encodeURIComponent(auctionUuid)}/items/${itemId}/qr`, {
      responseType: 'blob',
    });
    return resp.data as Blob;
  },

  async bulkPublish(auctionUuid: string, itemIds: number[]): Promise<BulkActionResult[]> {
    const resp = await httpClient().post<{ results: BulkActionResult[] }>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/bulk-publish`,
      { itemIds }
    );
    return resp.data.results;
  },

  async bulkWithdraw(auctionUuid: string, itemIds: number[], reason?: string): Promise<BulkActionResult[]> {
    const resp = await httpClient().post<{ results: BulkActionResult[] }>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/bulk-withdraw`,
      { itemIds, reason }
    );
    return resp.data.results;
  },

  async bulkDelete(auctionUuid: string, itemIds: number[]): Promise<BulkActionResult[]> {
    const resp = await httpClient().post<{ results: BulkActionResult[] }>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/bulk-delete`,
      { itemIds }
    );
    return resp.data.results;
  },

  /**
   * Re-stamp the auction's current pricing defaults onto the given rows.
   * All-or-nothing server-side, so this resolves with the whole verdict or
   * rejects — there is no partial outcome to report.
   */
  async reapplyDefaults(
    auctionUuid: string,
    itemIds: number[],
    bundleIds: number[]
  ): Promise<ReapplyDefaultsResponse> {
    const resp = await httpClient().post<ReapplyDefaultsResponse>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/reapply-defaults`,
      { itemIds, bundleIds }
    );
    return resp.data;
  },

  async listCategories(auctionUuid: string): Promise<Category[]> {
    const resp = await httpClient().get<Category[] | { items: Category[] }>(`/admin/auctions/${encodeURIComponent(auctionUuid)}/categories`);
    return Array.isArray(resp.data) ? resp.data : resp.data.items;
  },

  async createCategory(auctionUuid: string, name: string): Promise<Category> {
    const resp = await httpClient().post<Category>(`/admin/auctions/${encodeURIComponent(auctionUuid)}/categories`, {
      name,
    });
    return resp.data;
  },

  async requestPhotoUpload(auctionUuid: string, itemId: number, filename: string): Promise<PresignedSlot> {
    const resp = await httpClient().post<PresignedSlot>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/items/${itemId}/photos`,
      { filename }
    );
    return resp.data;
  },
  /** GT1-446 — same slot contract as item photos, keyed under bundle-{id}. */
  async requestBundlePhotoUpload(auctionUuid: string, bundleId: number, filename: string): Promise<PresignedSlot> {
    const resp = await httpClient().post<PresignedSlot>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/bundles/${bundleId}/photos`,
      { filename }
    );
    return resp.data;
  },
};
